<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { useRegistry, useContext, useDialogs } from '@hantera/portal-app'
import { DropdownButton, DropdownItem, TextInput } from '@hantera/design-system'
import { renderLanguage, SUPPORTED_LOCALES } from '@/constants/locales'

const context = useContext()
const dialogs = useDialogs()
const registry = useRegistry()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = 'Locales'
  }
})

// Permission checks
const canRead = computed(() => context.authorize('registry/locales:read'))
const canWrite = computed(() => context.authorize('registry/locales:write'))

// Data structure
interface Locale {
  key: string
  label: string
  language: string
  formatting: string
}

const locales = ref<Locale[]>([])
const loading = ref(true)

// Language options: ALL supported locales (base + regional)
const languageOptions = computed(() => {
  return SUPPORTED_LOCALES
    .map(locale => ({
      code: locale,
      name: renderLanguage(locale) || locale
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

// Formatting options: ONLY regional locales (with hyphen)
const formattingOptions = computed(() => {
  return SUPPORTED_LOCALES
    .filter(locale => locale.includes('-'))
    .map(locale => ({
      code: locale,
      name: renderLanguage(locale) || locale,
      numberFormatter: new Intl.NumberFormat(locale),
      dateTimeFormatter: new Intl.DateTimeFormat(locale)
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

// Filters
const languageFilter = ref('')
const formattingFilter = ref('')

const filteredLanguageOptions = computed(() =>
  languageOptions.value.filter(opt =>
    opt.name.toLowerCase().includes(languageFilter.value.toLowerCase()) ||
    opt.code.toLowerCase().includes(languageFilter.value.toLowerCase())
  )
)

const filteredFormattingOptions = computed(() =>
  formattingOptions.value.filter(opt =>
    opt.name.toLowerCase().includes(formattingFilter.value.toLowerCase()) ||
    opt.code.toLowerCase().includes(formattingFilter.value.toLowerCase())
  )
)

// Load locales
async function loadLocales() {
  loading.value = true
  try {
    const data = await registry.list('locales/', false)

    locales.value = Object.keys(data).map(fullKey => {
      const key = fullKey.split('/').pop() || ''
      const locale = data[fullKey]
      return {
        key,
        label: locale.label || '',
        language: locale.language || '',
        formatting: locale.formatting || ''
      }
    }).sort((a, b) => a.key.localeCompare(b.key))
  } catch (e) {
    console.error('Failed to load locales:', e)
  } finally {
    loading.value = false
  }
}

loadLocales()

// Auto-save on change
async function saveLocale(locale: Locale) {
  if (!canWrite.value) return

  try {
    await registry.set(`locales/${locale.key}`, {
      label: locale.label,
      language: locale.language,
      formatting: locale.formatting
    })
  } catch (e) {
    console.error('Failed to save locale:', e)
    await dialogs.alert('Failed to save locale. Please try again.', 'Error')
  }
}

// Add new locale with smart defaults
async function addLocale() {
  const key = prompt('Enter locale key (e.g., "en", "sv", "de"):')
  if (!key || key.trim() === '') return

  const normalizedKey = key.trim()

  const LOCALE_KEY_PATTERN = /^[a-z_][a-z0-9_]*$/i
  if (!LOCALE_KEY_PATTERN.test(normalizedKey)) {
    await dialogs.alert(
      'Invalid locale. May only include a-z, 0-9, _ and must start with a non-numeric character.',
      'Invalid Key'
    )
    return
  }

  // Check if already exists
  if (locales.value.some(l => l.key === normalizedKey)) {
    await dialogs.alert('A locale with this key already exists.', 'Duplicate Key')
    return
  }

  // Smart defaults based on locale code
  let language = ''
  let formatting = ''

  // ONLY set if key is a known supported locale
  if (SUPPORTED_LOCALES.includes(normalizedKey)) {
    language = normalizedKey
    // If it's a regional locale (has hyphen), also set formatting
    if (normalizedKey.includes('-')) {
      formatting = normalizedKey
    }
  }

  const newLocale: Locale = {
    key: normalizedKey,
    label: '',
    language,
    formatting
  }

  try {
    await registry.set(`locales/${normalizedKey}`, {
      label: newLocale.label,
      language: newLocale.language,
      formatting: newLocale.formatting
    })

    await loadLocales()
  } catch (e) {
    console.error('Failed to create locale:', e)
    await dialogs.alert('Failed to create locale. Please try again.', 'Error')
  }
}

// Delete locale
async function deleteLocale(locale: Locale) {
  const confirmed = await dialogs.confirm(
    `Are you sure you want to delete locale "${locale.key}"?`,
    'Delete Locale',
    'Delete',
    'Cancel',
    true
  )

  if (!confirmed) return

  try {
    await registry.clear(`locales/${locale.key}`)
    await loadLocales()
  } catch (e) {
    console.error('Failed to delete locale:', e)
    await dialogs.alert('Failed to delete locale. Please try again.', 'Error')
  }
}
</script>

<template>
  <div class="locales-view" v-if="canRead">
    <div class="header">
      <h1>Locales</h1>
      <button 
        v-if="canWrite" 
        @click="addLocale" 
        class="btn-primary"
      >
        Add Locale
      </button>
    </div>
    
    <div v-if="loading" class="loading">Loading...</div>
    
    <div v-else class="locales-table-container">
      <table class="locales-table" v-if="locales.length > 0">
        <thead>
          <tr>
            <th>Key</th>
            <th>Label</th>
            <th>Language</th>
            <th>Formatting</th>
            <th v-if="canWrite" class="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="locale in locales" :key="locale.key">
            <td class="key-column">
              <code>{{ locale.key }}</code>
            </td>
            <td>
              <input
                v-model="locale.label"
                @change="saveLocale(locale)"
                :disabled="!canWrite"
                placeholder="Label in locale's language"
                class="input"
              />
            </td>
            <td>
              <DropdownButton 
                v-if="canWrite"
                :label="locale.language ? renderLanguage(locale.language) || locale.language : 'Select language'"
                class="dropdown-full-width"
              >
                <div class="toolbar">
                  <TextInput v-model="languageFilter" placeholder="Filter languages..." />
                </div>
                <div class="dropdown-scroll">
                  <DropdownItem
                    v-for="option in filteredLanguageOptions"
                    :key="option.code"
                    @click="locale.language = option.code; saveLocale(locale); languageFilter = ''"
                  >
                    {{ option.name }}
                  </DropdownItem>
                </div>
              </DropdownButton>
              <span v-else>{{ locale.language ? renderLanguage(locale.language) : '-' }}</span>
            </td>
            <td>
              <DropdownButton 
                v-if="canWrite"
                :label="locale.formatting ? renderLanguage(locale.formatting) || locale.formatting : 'Select formatting'"
                class="dropdown-full-width"
              >
                <div class="toolbar">
                  <TextInput v-model="formattingFilter" placeholder="Filter formats..." />
                </div>
                <div class="formats dropdown-scroll">
                  <DropdownItem
                    v-for="format in filteredFormattingOptions"
                    :key="format.code"
                    @click="locale.formatting = format.code; saveLocale(locale); formattingFilter = ''"
                  >
                    <div>{{ format.name }}</div>
                    <div>{{ format.dateTimeFormatter.format(new Date()) }}</div>
                    <div>{{ format.numberFormatter.format(12345.6789) }}</div>
                  </DropdownItem>
                </div>
              </DropdownButton>
              <span v-else>{{ locale.formatting ? renderLanguage(locale.formatting) : '-' }}</span>
            </td>
            <td v-if="canWrite" class="actions-column">
              <button 
                @click="deleteLocale(locale)" 
                class="btn-danger"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-else class="empty-state">
        <p>No locales defined.</p>
        <button 
          v-if="canWrite" 
          @click="addLocale" 
          class="btn-primary"
        >
          Add First Locale
        </button>
      </div>
    </div>
  </div>
  
  <div v-else class="no-permission">
    <p>You don't have permission to view locales.</p>
  </div>
</template>

<style scoped>
.locales-view {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
}

.loading {
  padding: 40px;
  text-align: center;
  color: var(--color-text-secondary);
}

.locales-table-container {
  border: 1px solid var(--color-border);
  border-radius: 4px;
  overflow: hidden;
  background: var(--color-background);
}

.locales-table {
  width: 100%;
  border-collapse: collapse;
}

.locales-table thead {
  background: var(--color-background-secondary);
}

.locales-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 500;
  border-bottom: 1px solid var(--color-border);
}

.locales-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
}

.locales-table tbody tr:last-child td {
  border-bottom: none;
}

.locales-table tbody tr:hover {
  background: var(--color-background-tertiary);
}

.key-column code {
  font-family: monospace;
  padding: 2px 6px;
  background: var(--color-background-secondary);
  border-radius: 3px;
  font-size: 0.9em;
}

.dropdown-full-width {
  width: 100%;
}

.dropdown-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.formats {
  display: grid;
  grid-template-columns: max-content max-content max-content;
  column-gap: 5px;
}

.formats :deep(li),
.formats :deep(li>div) {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.actions-column {
  width: 100px;
  text-align: right;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.empty-state p {
  margin: 0 0 20px 0;
  color: var(--color-text-secondary);
  font-size: 16px;
}

.no-permission {
  padding: 60px 20px;
  text-align: center;
}

.no-permission p {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 16px;
}
</style>
